import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/App';

/**
 * Hook to manage resource locks
 * @param {string} resourceType - Type of resource ('booking', 'room', 'guest')
 * @param {string} resourceId - ID of the resource
 * @param {string} action - Action being performed ('checkin', 'checkout', 'edit', 'view')
 * @param {boolean} autoAcquire - Whether to automatically acquire lock on mount
 */
export function useResourceLock(resourceType, resourceId, action, autoAcquire = false) {
  const [lockStatus, setLockStatus] = useState({
    isLocked: false,
    lockedBy: null,
    canAcquire: true,
    expiresAt: null,
    hasLock: false
  });
  const [loading, setLoading] = useState(false);
  const heartbeatRef = useRef(null);
  const lockIdRef = useRef(null);

  // Check lock status
  const checkLockStatus = useCallback(async () => {
    if (!resourceId) return;
    
    try {
      const response = await api.get(`/locks/status/${resourceType}/${resourceId}`, {
        params: { action }
      });
      
      setLockStatus({
        isLocked: response.data.is_locked,
        lockedBy: response.data.locked_by_name || response.data.locked_by,
        canAcquire: response.data.can_acquire,
        expiresAt: response.data.expires_at,
        hasLock: response.data.can_acquire && response.data.is_locked
      });
    } catch (error) {
      console.error('Failed to check lock status:', error);
    }
  }, [resourceType, resourceId, action]);

  // Acquire lock
  const acquireLock = useCallback(async () => {
    if (!resourceId) return { success: false, message: 'No resource ID' };
    
    setLoading(true);
    try {
      const response = await api.post('/locks/acquire', {
        resource_type: resourceType,
        resource_id: resourceId,
        action
      });
      
      if (response.data.success) {
        lockIdRef.current = response.data.lock_id;
        setLockStatus(prev => ({
          ...prev,
          isLocked: true,
          hasLock: true,
          canAcquire: true,
          lockedBy: response.data.locked_by,
          expiresAt: response.data.expires_at
        }));
        
        // Start heartbeat to keep lock alive
        startHeartbeat();
      } else {
        setLockStatus(prev => ({
          ...prev,
          isLocked: true,
          hasLock: false,
          canAcquire: false,
          lockedBy: response.data.locked_by,
          expiresAt: response.data.expires_at
        }));
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to acquire lock:', error);
      return { success: false, message: 'Failed to acquire lock' };
    } finally {
      setLoading(false);
    }
  }, [resourceType, resourceId, action]);

  // Release lock
  const releaseLock = useCallback(async () => {
    if (!resourceId) return;
    
    // Stop heartbeat
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    
    try {
      await api.post('/locks/release', {
        resource_type: resourceType,
        resource_id: resourceId,
        action
      });
      
      lockIdRef.current = null;
      setLockStatus({
        isLocked: false,
        lockedBy: null,
        canAcquire: true,
        expiresAt: null,
        hasLock: false
      });
    } catch (error) {
      console.error('Failed to release lock:', error);
    }
  }, [resourceType, resourceId, action]);

  // Start heartbeat to keep lock alive
  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }
    
    // Renew lock every 5 minutes
    heartbeatRef.current = setInterval(async () => {
      try {
        await api.post('/locks/acquire', {
          resource_type: resourceType,
          resource_id: resourceId,
          action
        });
      } catch (error) {
        console.error('Failed to renew lock:', error);
      }
    }, 5 * 60 * 1000);
  }, [resourceType, resourceId, action]);

  // Auto-acquire lock on mount if requested
  useEffect(() => {
    if (autoAcquire && resourceId) {
      acquireLock();
    }
    
    // Cleanup on unmount
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      // Release lock on unmount
      if (lockIdRef.current) {
        api.post('/locks/release', {
          resource_type: resourceType,
          resource_id: resourceId,
          action
        }).catch(() => {});
      }
    };
  }, [autoAcquire, resourceId]);

  // Check status on mount
  useEffect(() => {
    if (resourceId && !autoAcquire) {
      checkLockStatus();
    }
  }, [resourceId, autoAcquire, checkLockStatus]);

  return {
    lockStatus,
    loading,
    acquireLock,
    releaseLock,
    checkLockStatus,
    isLocked: lockStatus.isLocked,
    canAcquire: lockStatus.canAcquire,
    hasLock: lockStatus.hasLock,
    lockedBy: lockStatus.lockedBy
  };
}

/**
 * Hook to check multiple resource locks at once (for list views)
 */
export function useResourceLocks(resources, resourceType, action) {
  const [locks, setLocks] = useState({});
  const [loading, setLoading] = useState(false);

  const checkAllLocks = useCallback(async () => {
    if (!resources || resources.length === 0) return;
    
    setLoading(true);
    const lockMap = {};
    
    try {
      await Promise.all(
        resources.map(async (resource) => {
          const resourceId = resource.id || resource._id;
          try {
            const response = await api.get(`/locks/status/${resourceType}/${resourceId}`, {
              params: { action }
            });
            lockMap[resourceId] = {
              isLocked: response.data.is_locked,
              lockedBy: response.data.locked_by_name || response.data.locked_by,
              canAcquire: response.data.can_acquire
            };
          } catch {
            lockMap[resourceId] = { isLocked: false, lockedBy: null, canAcquire: true };
          }
        })
      );
      setLocks(lockMap);
    } catch (error) {
      console.error('Failed to check locks:', error);
    } finally {
      setLoading(false);
    }
  }, [resources, resourceType, action]);

  useEffect(() => {
    checkAllLocks();
    
    // Poll for lock updates every 30 seconds
    const interval = setInterval(checkAllLocks, 30000);
    return () => clearInterval(interval);
  }, [checkAllLocks]);

  return { locks, loading, refresh: checkAllLocks };
}

export default useResourceLock;
